import * as Sentry from "@sentry/react"
import i18n from "i18next"

import type {
  EntityType,
  MapCluster,
  MapFeature,
  MapFeaturesResponse
} from "../redux/global-map.types"
import { entityIconSvgMarkup } from "./entity-icon-colors.ts"

// `@googlemaps/js-api-loader` (Task 17) brings in `@types/google.maps` as a
// dependency, which declares the `google` namespace ambiently/globally — no
// import needed here, and `google.maps.Map` below is the real SDK type.

interface RenderOverlaysOptions {
  // Only `mode`/`features`/`clusters` are ever read below — narrowed so a
  // lighter object (e.g. the home-dashboard map widget's own payload,
  // reshaped) satisfies it too, without fabricating the full page's
  // `query_fingerprint`/`viewport_total`/`unmapped`/etc. fields this never
  // uses. Mirrors the same narrowing on `MapCanvasProps.data`.
  data: Pick<MapFeaturesResponse, "mode" | "features" | "clusters">
  selectedId: string | null
  onSelectFeature: (id: string, entityType: EntityType) => void
  onClusterClick: (cluster: MapCluster) => void
  // Task 20 — analogous to `onSelectFeature` above, but for hover rather
  // than click: called with `(id, rect)` on mouseenter (`rect` is the
  // marker's own `content` element's live `getBoundingClientRect()`, taken
  // at the moment the mouse enters it — the real screen position, no
  // Maps-projection math needed since `content` is a plain, already-visible
  // `HTMLElement`) and `(null, null)` on mouseleave. Optional — every
  // existing caller/test that doesn't pass it keeps working unchanged.
  // Wired only onto individual feature markers, never cluster markers (a
  // cluster has no single reference/title/customer to show).
  onHoverFeature?: (id: string | null, rect: DOMRect | null) => void
}

// The marker's accessible name — never colour alone (a11y / spec). Takes the
// four fields it needs rather than a full `MapFeature` so it stays usable
// wherever only a partial record is on hand (e.g. a quick-view payload).
// Routed through the `i18n` singleton rather than a passed-in `TFunction`:
// this runs from `renderOverlays`, deep inside a Google Maps callback with no
// React render cycle nearby to source one from — the same shape as the
// `i18n.t()` calls in `projects.thunks.ts` / `project-checklist.thunks.ts`.
//
// `entity_type` is interpolated into the name itself (not just conveyed by
// the glyph, which is `aria-hidden`) — a screen-reader user can otherwise not
// tell a project marker from a lead marker once zoomed past clustering, since
// a status label like "On Hold" exists on both entity types.
function markerLabel(
  feature: Pick<
    MapFeature,
    "title" | "status_label" | "precision" | "entity_type"
  >
): string {
  const approximateSuffix =
    feature.precision === "approximate"
      ? i18n.t("globalMap.markers.approximateSuffix")
      : ""

  return (
    i18n.t("globalMap.markers.accessibleName", {
      title: feature.title,
      entityLabel: i18n.t(`globalMap.entityLabel.${feature.entity_type}`),
      statusLabel: feature.status_label
    }) + approximateSuffix
  )
}

const MARKER_BASE_CLASS = "global-map-marker"
const SELECTED_Z_INDEX = 999

// Project = house glyph in a rounded square (circle, per the spec's marker
// grammar); lead = pin glyph — the two entity types stay visually distinct by
// shape/glyph, never by colour alone (a11y / spec). Approximate precision is
// an outlined variant of whichever shape the entity already has.
const ENTITY_GLYPH: Record<EntityType, string> = {
  project: "⌂",
  lead: "📍"
}

// Shared by the map marker (below) and `ResultsList`'s row glyph — the
// keyboard/screen-reader list has to read as the same shape/glyph grammar as
// the marker it stands in for, so both read from this one map rather than
// each keeping their own copy.
function entityGlyph(entityType: EntityType): string {
  return ENTITY_GLYPH[entityType]
}

// The icon is decorative (`aria-hidden`) — the marker's own `aria-label`
// (via `markerLabel`) carries the accessible name. `--project`/`--lead`
// (`markers.less`) supply the colored-square background + white icon color;
// the SVG markup itself is `entity-icon-colors.ts`'s raw path data, kept in
// sync with `Icon`'s own "Projects"/"Leads" components since this plain-DOM
// marker content has no React tree to mount that component into.
function createFeatureMarkerContent(
  feature: MapFeature,
  isSelected: boolean
): HTMLElement {
  const el = document.createElement("div")
  el.className = [
    MARKER_BASE_CLASS,
    `${MARKER_BASE_CLASS}--${feature.entity_type}`,
    feature.precision === "approximate"
      ? `${MARKER_BASE_CLASS}--approximate`
      : "",
    isSelected ? `${MARKER_BASE_CLASS}--selected` : ""
  ]
    .filter(Boolean)
    .join(" ")
  el.setAttribute("role", "img")
  el.setAttribute("aria-label", markerLabel(feature))

  const icon = document.createElement("span")
  icon.className = `${MARKER_BASE_CLASS}__icon`
  icon.setAttribute("aria-hidden", "true")
  icon.innerHTML = entityIconSvgMarkup(feature.entity_type)
  el.appendChild(icon)

  return el
}

// A cluster mixing both entity types needs an accessible name that says so
// ("12 leads, 8 projects" — leads first, per the spec) rather than the plain
// project-only count Increment 1 always sent. A single-entity cluster keeps
// its own plain count (`clusterCount` for projects, `clusterCountLeads` for
// leads) instead of routing through the mixed phrasing with a zero side —
// "20 records: 0 leads, 20 projects" is nonsensical.
function clusterLabel(cluster: MapCluster): string {
  const { count, project_count, lead_count } = cluster

  if (project_count > 0 && lead_count > 0) {
    return i18n.t("globalMap.markers.mixedClusterCount", {
      count,
      leadCount: lead_count,
      projectCount: project_count
    })
  }

  if (lead_count > 0 && project_count === 0) {
    return i18n.t("globalMap.markers.clusterCountLeads", { count })
  }

  return i18n.t("globalMap.markers.clusterCount", { count })
}

// Presentation only — reads the `project_count`/`lead_count` the cluster
// already carries to pick a colour class, so a lead-only cluster is blue, a
// project-only one green and a mixed one the app's teal. No clustering
// decision is made here; the server still decides what is clustered.
function clusterToneClass(cluster: MapCluster): string {
  const { project_count, lead_count } = cluster
  if (project_count > 0 && lead_count > 0) return `${MARKER_BASE_CLASS}--cluster-mixed`
  if (project_count > 0) return `${MARKER_BASE_CLASS}--cluster-project`
  if (lead_count > 0) return `${MARKER_BASE_CLASS}--cluster-lead`
  return ""
}

function createClusterMarkerContent(cluster: MapCluster): HTMLElement {
  const el = document.createElement("div")
  el.className = [
    MARKER_BASE_CLASS,
    `${MARKER_BASE_CLASS}--cluster`,
    clusterToneClass(cluster)
  ]
    .filter(Boolean)
    .join(" ")
  el.setAttribute("role", "img")
  el.setAttribute("aria-label", clusterLabel(cluster))
  el.textContent = String(cluster.count)
  return el
}

// One `AdvancedMarkerElement` per feature/cluster in the current response.
// Returns a disposer that detaches every marker from the map and removes its
// listeners — callers (Task 17's canvas) run it before the next render pass
// so stale overlays never linger across a data refresh.
function renderOverlays(
  map: google.maps.Map,
  opts: RenderOverlaysOptions
): () => void {
  const { data, selectedId, onSelectFeature, onClusterClick, onHoverFeature } =
    opts
  const disposers: Array<() => void> = []

  const addMarker = (
    position: { lat: number; lng: number },
    content: HTMLElement,
    zIndex: number | undefined,
    onClick: () => void,
    hover?: { onEnter: () => void; onLeave: () => void }
  ) => {
    // `AdvancedMarkerElement`'s constructor reaches into `map`'s own
    // internal state and can throw synchronously — confirmed live when the
    // underlying `google.maps.Map` never finished initializing (e.g. the
    // browser key is blocked for this referrer, a failure Google does not
    // route through `gm_authFailure`, unlike a bad/missing key). This runs
    // inside a React passive effect (`MapCanvas`'s `renderOverlays` call),
    // so an uncaught throw here takes down the whole app via the route
    // error boundary — one broken marker must never do that. Reported to
    // Sentry and skipped, rather than silently swallowed, since a marker
    // that fails to render is itself worth knowing about.
    let marker: google.maps.marker.AdvancedMarkerElement
    try {
      marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position,
        content,
        zIndex
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { globalMapMarkerCreate: true } })
      return
    }
    const listener = marker.addListener("click", onClick)

    // `AdvancedMarkerElement.content` is a plain `HTMLElement` — Google's
    // docs call out that it supports ordinary DOM listeners directly, so
    // this attaches native `mouseenter`/`mouseleave` rather than reaching for
    // a Maps-specific hover API (there isn't one for this element).
    if (hover) {
      content.addEventListener("mouseenter", hover.onEnter)
      content.addEventListener("mouseleave", hover.onLeave)
    }

    disposers.push(() => {
      listener?.remove?.()
      if (hover) {
        content.removeEventListener("mouseenter", hover.onEnter)
        content.removeEventListener("mouseleave", hover.onLeave)
      }
      marker.map = null
    })
  }

  if (data.mode === "features") {
    data.features.forEach((feature) => {
      const isSelected = feature.id === selectedId
      const content = createFeatureMarkerContent(feature, isSelected)
      addMarker(
        { lat: feature.latitude, lng: feature.longitude },
        content,
        isSelected ? SELECTED_Z_INDEX : undefined,
        () => onSelectFeature(feature.id, feature.entity_type),
        onHoverFeature
          ? {
              onEnter: () =>
                onHoverFeature(feature.id, content.getBoundingClientRect()),
              onLeave: () => onHoverFeature(null, null)
            }
          : undefined
      )
    })
  } else {
    data.clusters.forEach((cluster) => {
      addMarker(
        { lat: cluster.latitude, lng: cluster.longitude },
        createClusterMarkerContent(cluster),
        undefined,
        () => onClusterClick(cluster)
      )
    })
  }

  return () => {
    disposers.forEach((dispose) => dispose())
  }
}

export { markerLabel, renderOverlays, entityGlyph }
export type { RenderOverlaysOptions }
